/**
 * The mobile app's capabilities, apart from every other service's: the
 * entry point the app imports (`@libs/environment-client-contracts/mobile`). Its
 * bundle then carries only these features and the gates' core, so a change
 * to another service's capabilities does not change the app.
 *
 * `mobileService` selects exactly these features, and the full registry
 * spreads the same definitions into each capability
 * (`src/mobile/mobile.test.ts` checks both).
 */
import { ads } from "../capabilities/ads";
import { doppler } from "../capabilities/doppler";
import { googleAuth } from "../capabilities/google-auth";
import { mobileApp } from "../capabilities/mobile-app";
import { branchMobileFeatures } from "../capabilities/mobile/branch";
import { customerioMobileFeatures } from "../capabilities/mobile/customerio";
import { deploymentContextMobileFeatures } from "../capabilities/mobile/deployment-context";
import { easMobileFeatures } from "../capabilities/mobile/eas";
import { firebaseMobileFeatures } from "../capabilities/mobile/firebase";
import { gcpMobileFeatures } from "../capabilities/mobile/gcp";
import { intercomMobileFeatures } from "../capabilities/mobile/intercom";
import { revenuecatMobileFeatures } from "../capabilities/mobile/revenuecat";
import { rudderstackMobileFeatures } from "../capabilities/mobile/rudderstack";
import { sentryMobileFeatures } from "../capabilities/mobile/sentry";
import { siteMobileFeatures } from "../capabilities/mobile/site";
import { stripeMobileFeatures } from "../capabilities/mobile/stripe";
import { web3MobileFeatures } from "../capabilities/mobile/web3";
import {
  createRuntimeGates,
  type GateNameOf,
  type RuntimeGates,
} from "@libs/runtime-contracts/runtime-gates";
import type { CapabilitySelection } from "../types";

/** Every capability feature the mobile service selects, in its order. */
export const mobileCapabilityDefinitions = {
  doppler: { runtimeConfig: doppler.definitions.runtimeConfig },
  site: siteMobileFeatures,
  deploymentContext: deploymentContextMobileFeatures,
  firebase: firebaseMobileFeatures,
  branch: branchMobileFeatures,
  customerio: customerioMobileFeatures,
  eas: easMobileFeatures,
  sentry: sentryMobileFeatures,
  stripe: stripeMobileFeatures,
  googleAuth: googleAuth.definitions,
  ads: ads.definitions,
  intercom: intercomMobileFeatures,
  mobileApp: mobileApp.definitions,
  revenuecat: revenuecatMobileFeatures,
  rudderstack: rudderstackMobileFeatures,
  web3: web3MobileFeatures,
  gcp: gcpMobileFeatures,
};

export type MobileGateName = GateNameOf<typeof mobileCapabilityDefinitions>;

/** The mobile service's selections: one per feature above. */
export const mobileCapabilities: readonly CapabilitySelection[] = Object.entries(
  mobileCapabilityDefinitions,
).flatMap(([capability, features]) =>
  Object.keys(features).map((feature) => ({ capability, features: [feature] })),
);

/** The app's runtime gates for its env (`EXPO_PUBLIC_LISSEN_ENV` as LISSEN_ENV). */
export function createMobileRuntimeGates(
  env: Record<string, string | undefined>,
): RuntimeGates<MobileGateName> {
  return createRuntimeGates<MobileGateName>({
    definitions: mobileCapabilityDefinitions,
    selections: mobileCapabilities,
    env,
  });
}
